import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  XMarkIcon,
  HomeIcon,
  ChartBarIcon,
  ArrowsRightLeftIcon,
  UsersIcon,
  ArrowRightOnRectangleIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  ChartPieIcon,
  TrophyIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  BanknotesIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import useTranslation from '../services/translationService';
import { useSettingsModal } from './SettingsModalContext';
import { isUserRestricted } from '../services/breakglassService';

const USER_TYPES = {
  ADMIN: 'admin',
  COUNTRY_ADMIN: 'country',
  CITY_ADMIN: 'city',
  SUPER_AGENT: 'super-agent',
  AGENT: 'agent',
  PLAYER: 'player'
};

const TopNav = ({ isOpen, onClose, onSignOut, userType, username }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { openSettings, closeSettings } = useSettingsModal();
  const [isRestricted, setIsRestricted] = useState(false);

  const isPlaywinadmin = username === 'playwinadmin';

  console.log('TopNav userType:', userType); // Debug log

  // Check if user is in restricted mode
  useEffect(() => {
    const checkRestriction = async () => {
      const restricted = await isUserRestricted();
      setIsRestricted(restricted);
    };
    checkRestriction();
  }, []);

  // While open, keep the page behind from scrolling and close on Escape
  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  // Get the type one level below the current user's type
  const getSubordinateType = (currentType) => {
    console.log('Getting subordinate type for:', currentType); // Debug log
    switch (currentType) {
      case USER_TYPES.ADMIN:
        return USER_TYPES.COUNTRY_ADMIN;
      case USER_TYPES.COUNTRY_ADMIN:
        return USER_TYPES.CITY_ADMIN;
      case USER_TYPES.CITY_ADMIN:
        return USER_TYPES.SUPER_AGENT;
      case USER_TYPES.SUPER_AGENT:
        return USER_TYPES.AGENT;
      case USER_TYPES.AGENT:
        return USER_TYPES.PLAYER;
      default:
        return null;
    }
  };

  const menuItems = [];

  if (isRestricted) {
    // Restricted users only get Reports and Agent Tree
    menuItems.push(
      {
        label: t('navigation.reports'),
        path: '/reports',
        icon: ChartBarIcon
      },
      {
        label: t('navigation.agentTree'),
        path: '/agent-tree',
        icon: UserGroupIcon
      }
    );
  } else {
    menuItems.push(
      {
        label: t('navigation.dashboard'),
        path: '/dashboard',
        icon: HomeIcon
      },
      {
        label: t('navigation.reports'),
        path: '/reports',
        icon: ChartBarIcon
      }
    );

    if (userType === USER_TYPES.ADMIN) {
      menuItems.push({
        label: t('navigation.adminAnalytics', 'Admin Analytics'),
        path: '/admin-analytics',
        icon: ChartPieIcon
      });
    }

    // Add Admin Statistics (3% sports GGR tracker) for playwinadmin only
    if (isPlaywinadmin) {
      menuItems.push({
        label: 'Admin Statistics',
        path: '/admin-statistics',
        icon: BanknotesIcon
      });
    }

    // Add IP Monitoring for admin and country admin users
    if ([USER_TYPES.ADMIN, USER_TYPES.COUNTRY_ADMIN].includes(userType)) {
      menuItems.push({
        label: t('navigation.monitoring', 'IP Monitoring'),
        path: '/monitoring',
        icon: ShieldExclamationIcon
      });
    }

    // Add Limits for admin, country, and city users
    if ([USER_TYPES.ADMIN, USER_TYPES.COUNTRY_ADMIN, USER_TYPES.CITY_ADMIN].includes(userType)) {
      menuItems.push({
        label: t('navigation.limits'),
        path: '/limits',
        icon: ShieldCheckIcon
      });
    }

    // Add remaining menu items
    menuItems.push(
      {
        label: t('navigation.betHistory'),
        path: '/bet-history',
        icon: TrophyIcon
      },
      {
        label: t('navigation.transactions'),
        path: '/transactions',
        icon: ArrowsRightLeftIcon
      }
    );

    // Add Agent Tree only if user is not an agent
    if (userType !== USER_TYPES.AGENT) {
      menuItems.push({
        label: t('navigation.agentTree'),
        path: '/agent-tree',
        icon: UserGroupIcon
      });
    }

    menuItems.push(
      {
        label: t('navigation.playerList'),
        path: '/agent-tree?filter=player',
        icon: UsersIcon
      },
      {
        label: t('navigation.settings'),
        action: () => {
          onClose();
          openSettings();
        },
        icon: Cog6ToothIcon
      }
    );
  }

  console.log('Final menuItems:', menuItems.map(item => item.label)); // Debug log

  // Most specific match wins, so Player List (/agent-tree?filter=player) doesn't also light up Agent Tree
  const currentParams = new URLSearchParams(location.search);
  const activeItem = menuItems
    .filter((item) => {
      if (!item.path) return false;
      const [pathname, query] = item.path.split('?');
      if (pathname !== location.pathname) return false;
      return [...new URLSearchParams(query)].every(([key, value]) => currentParams.get(key) === value);
    })
    .sort((a, b) => b.path.length - a.path.length)[0];

  if (!isOpen) return null;

  const handleSignOut = () => {
    onClose();
    navigate('/logout');
  };

  const handleNavigation = (item) => {
    // Close settings modal if it's open
    closeSettings();
    
    if (item.action) {
      item.action();
      return;
    }

    let path = item.path;
    // Agent Tree opens filtered to the level below the current user; restricted users get the full tree
    if (path === '/agent-tree' && !isRestricted) {
      const subordinateType = getSubordinateType(userType);
      if (subordinateType) {
        path = `/agent-tree?filter=${subordinateType}`;
      }
    }
    navigate(path);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="app-menu-title">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/40 dark:bg-black/60 animate-overlay-in motion-reduce:animate-none"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-over panel, anchored to the reading-start edge so it follows the menu button in RTL */}
      <aside className="absolute inset-y-0 start-0 flex w-72 max-w-[85vw] flex-col rounded-e-2xl bg-white shadow-2xl dark:bg-gray-800 dark:ring-1 dark:ring-white/10 animate-drawer-in rtl:animate-drawer-in-rtl motion-reduce:animate-none">
        <div className="flex items-center justify-between gap-3 px-4 pb-2 pt-[calc(env(safe-area-inset-top)+0.75rem)]">
          <h2 id="app-menu-title" className="text-base font-semibold text-gray-900 dark:text-white">
            {t('navigation.menu')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="-me-2 flex h-10 w-10 items-center justify-center rounded-full bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:bg-transparent dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-gray-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
            aria-label={t('common.close')}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {isRestricted && (
          <div className="mx-3 mb-2 flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
            <LockClosedIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            {t('navigation.restrictedMode', 'Read-Only Mode Active')}
          </div>
        )}

        <nav className="flex-1 overflow-y-auto overscroll-contain px-3 py-1">
          <ul className="space-y-0.5">
            {menuItems.map((item) => {
              const isActive = item === activeItem;
              return (
                <li key={item.label}>
                  <button
                    type="button"
                    onClick={() => handleNavigation(item)}
                    aria-current={isActive ? 'page' : undefined}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-start text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500
                      ${isActive
                        ? 'bg-teal-50 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300'
                        : 'bg-transparent text-gray-700 hover:bg-gray-100 active:bg-gray-200/70 dark:bg-transparent dark:text-gray-200 dark:hover:bg-white/5 dark:active:bg-white/10'}`}
                  >
                    <item.icon
                      className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500'}`}
                      aria-hidden="true"
                    />
                    <span className="truncate">{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Sign Out Button */}
        <div className="border-t border-gray-100 dark:border-white/10 px-3 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-start text-sm font-medium bg-transparent text-red-600 hover:bg-red-50 dark:bg-transparent dark:text-red-400 dark:hover:bg-red-500/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-500"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5 flex-shrink-0 rtl:-scale-x-100" aria-hidden="true" />
            {t('auth.signOut')}
          </button>
        </div>
      </aside>
    </div>
  );
};

export default TopNav; 
